import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import NewsNavbar from '@/components/feature/NewsNavbar';
import NewsFooter from '@/components/feature/NewsFooter';
import NewsCard from '@/components/feature/NewsCard';
import { useAllNews, useCategories } from '@/hooks/useNews';
import { useSEO } from '@/hooks/useSEO';

const PAGE_SIZE = 9;

export default function AllNewsPage() {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'views'>('date');
  const [page, setPage] = useState(1);

  const { news: allNews } = useAllNews();
  const { categories } = useCategories();

  const filtered = useMemo(() => {
    let list = activeCategory === 'all'
      ? allNews
      : allNews.filter(n => n.category === activeCategory);
    if (sortBy === 'views') {
      list = [...list].sort((a, b) => b.views - a.views);
    }
    return list;
  }, [activeCategory, sortBy, allNews]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useSEO({
    title: 'Tüm Teknoloji Haberleri - Güncel Haberler | TeknoNovaHub',
    description: `TeknoNovaHub'da ${allNews.length}+ teknoloji haberi. Yapay zeka, yazılım, girişimcilik, donanım ve daha fazlası. Günlük güncellenen içerikler.`,
    keywords: 'tüm teknoloji haberleri, güncel haberler, teknoloji arşivi, TeknoNovaHub',
    url: '/haberler',
    type: 'website',
  });

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] font-sans">
      <NewsNavbar />
      <main className="pt-28 md:pt-32">
        <section className="max-w-7xl mx-auto px-6 mb-8">
          <nav className="flex items-center gap-2 text-sm text-white/40 mb-4">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Ana Sayfa</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white">Tüm Haberler</span>
          </nav>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-white">Tüm Haberler</h1>
              <p className="text-white/50 text-sm mt-1">{filtered.length} haber listeleniyor</p>
            </div>
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full p-1">
              <button
                onClick={() => setSortBy('date')}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${sortBy === 'date' ? 'bg-white text-[#0A0E27]' : 'text-white/60 hover:text-white'}`}
              >
                <i className="ri-calendar-line mr-1.5" />En Yeni
              </button>
              <button
                onClick={() => setSortBy('views')}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${sortBy === 'views' ? 'bg-white text-[#0A0E27]' : 'text-white/60 hover:text-white'}`}
              >
                <i className="ri-fire-line mr-1.5" />En Popüler
              </button>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 mb-8">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => handleCategoryChange('all')}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${activeCategory === 'all' ? 'bg-white text-[#0A0E27]' : 'bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10'}`}
            >
              Tümü
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${activeCategory === cat.id ? 'text-[#0A0E27] font-bold' : 'bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10'}`}
                style={activeCategory === cat.id ? { backgroundColor: cat.color } : {}}
              >
                <i className={`${cat.icon} text-sm`} />{cat.name}
              </button>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 pb-8">
          {paginated.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginated.map(item => <NewsCard key={item.id} item={item} />)}
            </div>
          ) : (
            <div className="text-center py-16">
              <i className="ri-news-line text-5xl text-white/20 mb-4 block" />
              <p className="text-white/50">Bu kategoride haber bulunamadı.</p>
            </div>
          )}
        </section>

        {totalPages > 1 && (
          <section className="max-w-7xl mx-auto px-6 pb-16">
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-semibold transition-all cursor-pointer ${page === p ? 'bg-[#00D9FF] text-[#0A0E27]' : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'}`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
            <p className="text-center text-white/30 text-xs mt-3">Sayfa {page} / {totalPages} — Toplam {filtered.length} haber</p>
          </section>
        )}
      </main>
      <NewsFooter />
    </div>
  );
}
